// @ts-nocheck
import PropTypes from "prop-types";
import {useState} from "react";
import {useTranslation} from "react-i18next";
import {BaseButton, BaseInput, BaseSelect, toast} from "@/components";
import useGetUserWallet from "../../../hooks/wallet/useGetUserWallet";
import {useGetPaykuBanks, usePostPaykuWithdrawal} from "../../../hooks/wallet/usePostPaykuWithdrawal";
import useCurrencyStore from "../../../store/useCurrency.store";
import {formatCurrencyAmount} from "../../../util/balance.util";
import {buildPaykuWithdrawalSchema} from "./paykuWithdrawal.schema";

// Account type options for Chile
const CHILE_ACCOUNT_TYPES = {
  1: "Cuenta Corriente",
  2: "Cuenta Vista/RUT",
  3: "Cuenta de Ahorro",
};

const PaykuWithdrawal = ({onBack, onCancel}) => {
  const {t} = useTranslation();
  const selectedCurrency = useCurrencyStore(state => state.selectedCurrency);
  const {getAccountByCurrency} = useGetUserWallet();

  const [formData, setFormData] = useState({
    amount: "",
    beneficiaryName: "",
    email: "",
    phone: "",
    accountbank_name: "",
    accountbank_rut: "",
    accountbank_sbif: "",
    accountbank_type: 2, // Default to Cuenta Vista/RUT
    accountbank_num: "",
  });

  const {mutate: createWithdrawal, isPending, isError, error} = usePostPaykuWithdrawal();
  const {data: banksData, isLoading: banksLoading} = useGetPaykuBanks("CLP");

  // Only show Payku withdrawal for CLP currency
  const isValidCurrency = selectedCurrency?.id === "CLP";
  const currentCurrencyAccount = selectedCurrency ? getAccountByCurrency(selectedCurrency.id) : null;

  if (!isValidCurrency) {
    return (
      <div style={{textAlign: "center", padding: "2rem"}}>
        <h3>{t("withdrawalPaykuUnsupportedCurrency")}</h3>
        <p>{t("withdrawalPaykuOnlyCLP")}</p>
        <BaseButton
          variant="secondary"
          onClick={onBack}
          style={{marginTop: "1rem"}}
        >
          {t("depositInstructionsBack")}
        </BaseButton>
      </div>
    );
  }

  const availableBalance = currentCurrencyAccount?.balance || 0;
  const formattedBalance = formatCurrencyAmount(availableBalance, "CLP", "$");

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleBankSelect = bankCode => {
    const selectedBank = banksData?.data?.find(bank => bank.code === bankCode);
    if (selectedBank) {
      setFormData(prev => ({
        ...prev,
        accountbank_sbif: bankCode,
        accountbank_name: selectedBank.name,
      }));
    }
  };

  const handleSubmit = () => {
    const schema = buildPaykuWithdrawalSchema(availableBalance, t);
    const result = schema.safeParse(formData);

    if (!result.success) {
      for (const issue of result.error.issues) {
        toast.error(issue.message);
      }
      return;
    }

    const withdrawalData = {
      ...result.data,
      currency: "CLP",
    };

    createWithdrawal(withdrawalData, {
      onSuccess: response => {
        if (response.data?.success) {
          toast.success(t("withdrawalPaykuSuccess"));
          setTimeout(() => {
            if (onCancel) onCancel();
          }, 2000);
        } else {
          toast.error(response.data?.message || t("withdrawalPaykuError"));
        }
      },
      onError: error => {
        toast.error(error.message || t("withdrawalPaykuError"));
      },
    });
  };

  return (
    <div style={{padding: "1rem", maxWidth: "600px", margin: "0 auto"}}>
      <div style={{marginBottom: "2rem"}}>
        <h2>{t("withdrawalPaykuTitle")}</h2>
        <div style={{marginBottom: "1rem"}}>
          <p style={{color: "var(--text-secondary)"}}>
            {t("withdrawalPaykuAvailableBalance")}: <strong>{formattedBalance}</strong>
          </p>
        </div>
      </div>

      <form
        onSubmit={e => {
          e.preventDefault();
          handleSubmit();
        }}
        style={{display: "flex", flexDirection: "column", gap: "1rem"}}
      >
        <BaseInput
          label={t("withdrawalPaykuAmount")}
          type="number"
          value={formData.amount}
          onChange={e => handleInputChange("amount", e.target.value)}
          placeholder={t("withdrawalPaykuEnterAmount")}
          hint={`${t("withdrawalPaykuMinAmount")}: 1,000 CLP`}
          fullWidth
        />

        <BaseInput
          label={t("withdrawalPaykuBeneficiaryName")}
          value={formData.beneficiaryName}
          onChange={e => handleInputChange("beneficiaryName", e.target.value)}
          placeholder={t("withdrawalPaykuBeneficiaryNamePlaceholder")}
          fullWidth
        />

        <BaseInput
          label={t("withdrawalPaykuEmail")}
          type="email"
          value={formData.email}
          onChange={e => handleInputChange("email", e.target.value)}
          placeholder={t("withdrawalPaykuEmailPlaceholder")}
          fullWidth
        />

        <BaseInput
          label={t("withdrawalPaykuPhone")}
          value={formData.phone}
          onChange={e => handleInputChange("phone", e.target.value)}
          placeholder={t("withdrawalPaykuPhonePlaceholder")}
          fullWidth
        />

        <BaseInput
          label={t("withdrawalPaykuRUT")}
          value={formData.accountbank_rut}
          onChange={e => handleInputChange("accountbank_rut", e.target.value)}
          placeholder={t("withdrawalPaykuRUTPlaceholder")}
          hint={t("withdrawalPaykuRUTFormat")}
          fullWidth
        />

        <BaseSelect
          label={t("withdrawalPaykuBank")}
          value={formData.accountbank_sbif}
          onChange={e => handleBankSelect(e.target.value)}
          placeholder={t("withdrawalPaykuSelectBank")}
          disabled={banksLoading}
          options={(banksData?.data || []).map(bank => ({value: bank.code, label: bank.name}))}
        />

        <BaseSelect
          label={t("withdrawalPaykuAccountType")}
          value={String(formData.accountbank_type)}
          onChange={e => handleInputChange("accountbank_type", Number(e.target.value))}
          options={Object.entries(CHILE_ACCOUNT_TYPES).map(([value, label]) => ({value, label}))}
        />

        <BaseInput
          label={t("withdrawalPaykuAccountNumber")}
          value={formData.accountbank_num}
          onChange={e => handleInputChange("accountbank_num", e.target.value)}
          placeholder={t("withdrawalPaykuAccountNumberPlaceholder")}
          fullWidth
        />

        <div style={{display: "flex", gap: "1rem", marginTop: "2rem"}}>
          <BaseButton
            variant="secondary"
            type="button"
            onClick={onBack}
            style={{flex: 1}}
          >
            {t("depositInstructionsBack")}
          </BaseButton>
          <BaseButton
            variant="secondary"
            type="button"
            onClick={onCancel}
            style={{flex: 1}}
          >
            {t("depositInstructionsCancel")}
          </BaseButton>
          <BaseButton
            variant="primary"
            type="submit"
            loading={isPending}
            style={{flex: 1}}
          >
            {t("withdrawalPaykuSubmit")}
          </BaseButton>
        </div>

        {isError && error && (
          <div style={{marginTop: "1rem", color: "var(--color-danger-text)", textAlign: "center"}}>
            {error.message || t("withdrawalPaykuError")}
          </div>
        )}
      </form>
    </div>
  );
};

PaykuWithdrawal.propTypes = {
  onBack: PropTypes.func.isRequired,
  onCancel: PropTypes.func.isRequired,
};

export default PaykuWithdrawal;
